/** QuickRouter — Store zustand : projet ouvert, bibliothèque, sélection, outils d'édition,
 *  annuler/refaire, sauvegarde automatique, routage automatique et tour de l'assistant (SSE). */
import { create } from 'zustand';
import { api } from '../api/client.js';
import { makeLookup } from '../../shared/library/index.js';
import * as ops from '../../shared/ops.js';
import { polyBox } from '../../shared/geom.js';

const clone = (v) => JSON.parse(JSON.stringify(v));
const HISTORY_CAP = 80;

let saveTimer = null;
let transientSnapshot = null;
let eventSource = null;
let aiSnapshotPushed = false;

let lookupCache = { fps: null, fn: () => null };
function lookupFor(project) {
  const fps = project?.footprints ?? null;
  if (lookupCache.fps !== fps) lookupCache = { fps, fn: makeLookup(project) };
  return lookupCache.fn;
}

export const useProjectStore = create((set, get) => ({
  // ── État ──
  me: null,
  authError: null,
  projects: [],
  project: null,
  library: { footprints: [], templates: [] },
  selection: [],            // ids de composants, pistes, vias, textes, zones
  mode: 'sch',              // sch | board | 3d | fab — l'étude (schéma) d'abord
  schTool: 'select',        // select | wire | label | power
  schPower: 'GND',          // symbole posé par l'outil Alimentation
  schCenter: null,          // centre de la vue schéma (pose des nouveaux composants)
  leftTab: 'library',       // library | nets
  rightTab: 'props',        // props | chat
  tool: 'select',           // select | track | via
  layer: 'F',               // couche active (outil piste)
  show: { F: true, B: true, rats: true, zones: true, silk: true, pinNames: true, drc: true },
  highlightNet: null,
  fitNonce: 0,
  saveState: 'idle',
  statusText: '',
  past: [],
  future: [],
  drc: null,                // dernier résultat DRC (calculé dans le navigateur)
  routing: false,
  dialog: null,
  ai: { available: null, messages: [], job: null, text: '', status: '', tools: [], tier: 'fast', error: '' },

  lookup() { return lookupFor(get().project); },

  // ── Identité / projets / bibliothèque ──
  async loadMe() {
    try {
      set({ me: await api('/me'), authError: null });
      return true;
    } catch (e) {
      set({ me: null, authError: e.status === 401 || e.status === 403 ? { status: e.status, message: e.message } : null });
      return false;
    }
  },
  readOnly() { return get().project?.role === 'viewer'; },
  async loadProjects() { set({ projects: await api('/projects') }); },
  async loadLibrary() { set({ library: await api('/library') }); },
  async createProject(name, template = 'libre', params = {}) {
    const project = await api('/projects', { method: 'POST', body: { name, template, params } });
    await get().loadProjects();
    get().openLoaded(project);
  },
  async openProject(id) { get().openLoaded(await api(`/projects/${id}`)); },
  openLoaded(project) {
    set({
      project, selection: [], past: [], future: [], mode: 'sch', schTool: 'select', tool: 'select', saveState: 'idle', statusText: '', drc: null,
      highlightNet: null, fitNonce: get().fitNonce + 1,
      ai: { ...get().ai, messages: [], job: null, text: '', status: '', tools: [], error: '' },
    });
    get().loadLibrary().catch(() => {});
    get().loadChat().catch(() => {});
  },
  closeProject() {
    clearTimeout(saveTimer);
    eventSource?.close();
    eventSource = null;
    set({ project: null, selection: [], past: [], future: [] });
    get().loadProjects();
  },
  async shareProject(id, { sharedWith, viewers }) {
    await api(`/projects/${id}/share`, { method: 'PUT', body: { sharedWith, viewers } });
    await get().loadProjects();
  },
  async deleteProjectById(id) {
    await api(`/projects/${id}`, { method: 'DELETE' });
    await get().loadProjects();
  },

  // ── UI ──
  setMode: (mode) => set({ mode }),
  setLeftTab: (leftTab) => set({ leftTab }),
  setRightTab: (rightTab) => set({ rightTab }),
  setTool: (tool) => set({
    tool,
    statusText: {
      track: 'Piste : cliquez une pastille de départ, puis les points ; V change de couche (via) ; double-clic ou clic sur la pastille d\'arrivée pour finir ; Échap annule',
      via: 'Via : cliquez sur une piste ou une pastille pour poser un via du même net',
      select: '',
    }[tool] ?? '',
  }),
  setLayer: (layer) => set({ layer }),
  setSchTool: (schTool) => set({
    schTool,
    statusText: {
      wire: 'Fil : cliquez une broche ou un point, puis les coudes ; clic sur une broche ou un fil, double-clic ou Entrée pour finir ; Échap annule',
      label: 'Étiquette : cliquez une broche ou un fil, puis donnez le nom du net — même nom = même net',
      power: 'Alimentation : cliquez une broche ou un fil pour y poser le symbole choisi',
      select: '',
    }[schTool] ?? '',
  }),
  setSchPower: (schPower) => set({ schPower }),
  setSchCenter: (schCenter) => set({ schCenter }),
  toggleShow: (key) => set({ show: { ...get().show, [key]: !get().show[key] } }),
  setHighlightNet: (highlightNet) => set({ highlightNet }),
  fit: () => set({ fitNonce: get().fitNonce + 1 }),
  setSelection: (selection) => set({ selection: selection ?? [] }),
  select(id, additive = false) {
    if (!additive) return set({ selection: id ? [id] : [] });
    const s = get().selection;
    set({ selection: s.includes(id) ? s.filter((x) => x !== id) : [...s, id] });
  },
  setStatus: (statusText) => set({ statusText }),
  setDrc: (drc) => set({ drc }),
  openDialog: (dialog) => set({ dialog }),
  closeDialog: () => set({ dialog: null }),

  // ── Mutations avec historique ──
  mutate(fn) {
    const { project, past } = get();
    if (!project || get().ai.job || get().routing) return null;
    if (project.role === 'viewer') { set({ statusText: 'Projet en lecture seule : aucune modification possible.' }); return null; }
    const draft = clone(project);
    let out;
    try {
      out = fn(draft, lookupFor(draft));
    } catch (e) {
      set({ statusText: `⚠ ${e.message}` });
      return null;
    }
    set({ project: draft, past: [...past.slice(-HISTORY_CAP + 1), project], future: [] });
    get().scheduleSave();
    return out ?? true;
  },
  beginTransient() {
    if (transientSnapshot || !get().project || get().ai.job || get().project.role === 'viewer') return false;
    transientSnapshot = get().project;
    return true;
  },
  applyTransient(fn) {
    if (!transientSnapshot) return;
    const draft = clone(transientSnapshot);
    try { fn(draft, lookupFor(draft)); } catch { return; }
    set({ project: draft });
  },
  commitTransient() {
    if (!transientSnapshot) return;
    if (transientSnapshot !== get().project) {
      set({ past: [...get().past.slice(-HISTORY_CAP + 1), transientSnapshot], future: [] });
      get().scheduleSave();
    }
    transientSnapshot = null;
  },
  undo() {
    const { project, past, future } = get();
    if (!past.length || !project || get().ai.job) return;
    set({ project: past[past.length - 1], past: past.slice(0, -1), future: [project, ...future] });
    get().pruneSelection();
    get().scheduleSave();
  },
  redo() {
    const { project, past, future } = get();
    if (!future.length || !project || get().ai.job) return;
    set({ project: future[0], past: [...past, project], future: future.slice(1) });
    get().pruneSelection();
    get().scheduleSave();
  },
  pruneSelection() {
    const p = get().project;
    if (!p) return;
    const ids = new Set([...p.components, ...p.tracks, ...p.vias, ...p.texts, ...p.zones, ...(p.schematic?.wires ?? []), ...(p.schematic?.labels ?? [])].map((x) => x.id));
    set({ selection: get().selection.filter((id) => ids.has(id)) });
  },

  // ── Actions d'édition ──
  /** Pose une empreinte au centre de la carte (ou à pos). */
  placeFootprint(footprintId, pos) {
    const { project, mode, schCenter } = get();
    const [x0, y0, x1, y1] = polyBox(project.board.outline);
    const inSch = mode === 'sch' && project.schematic;
    // En schéma : symbole au centre de la vue, empreinte « à placer » à droite de la carte.
    const waiting = project.components.filter((c) => c.pos[0] > x1).length;
    const at = pos ?? (inSch
      ? [Math.round(x1 + 8 + Math.floor(waiting / 6) * 12), Math.round(y1 - 4 - (waiting % 6) * 9)] // colonnes contre le bord droit
      : [Math.round((x0 + x1) / 2), Math.round((y0 + y1) / 2)]);
    // Pas deux symboles au même point : décalage vers la droite tant que la place est prise.
    let schPos = null;
    if (inSch && schCenter) {
      schPos = [...schCenter];
      const taken = (q) => project.schematic.symbols.some((sy) => Math.abs(sy.pos[0] - q[0]) < 8 && Math.abs(sy.pos[1] - q[1]) < 8);
      for (let k = 0; k < 40 && taken(schPos); k++) schPos = [Math.round((schPos[0] + 12.7) * 1000) / 1000, schPos[1]];
    }
    const comp = get().mutate((p, lk) => ops.addComponent(p, lk, { footprint: footprintId, pos: at, ...(schPos ? { schPos } : {}) }));
    if (comp) {
      set({
        selection: [comp.id],
        statusText: inSch
          ? `${comp.ref} posé dans le schéma — glissez-le (R tourne, M miroir) ; son empreinte attend à droite de la carte`
          : `${comp.ref} posé — glissez-le à sa place (R tourne, F retourne)`,
      });
    }
  },
  deleteSelection() {
    const ids = new Set(get().selection);
    if (!ids.size) return;
    get().mutate((p) => {
      const comps = p.components.filter((c) => ids.has(c.id) && !c.locked).map((c) => c.ref);
      if (comps.length) ops.deleteComponents(p, comps);
      ops.deleteItems(p, [...ids]);
    });
    set({ selection: [] });
  },
  rotateSelection(delta = 90) {
    const ids = new Set(get().selection);
    get().mutate((p, lk) => {
      for (const c of p.components) if (ids.has(c.id) && !c.locked) ops.updateComponent(p, lk, c.id, { rot: c.rot + delta });
    });
  },
  flipSelection() {
    const ids = new Set(get().selection);
    get().mutate((p, lk) => {
      for (const c of p.components) if (ids.has(c.id) && !c.locked) ops.updateComponent(p, lk, c.id, { side: c.side === 'top' ? 'bottom' : 'top' });
    });
  },
  updateComponent(id, patch) { get().mutate((p, lk) => ops.updateComponent(p, lk, id, patch)); },
  updateProject(fn) { get().mutate((p, lk) => fn(p, lk)); },

  /** Routage automatique par le serveur (le projet est d'abord enregistré). */
  async autoroute(nets = null) {
    const { project } = get();
    if (!project || get().routing || get().ai.job || project.role === 'viewer') return;
    clearTimeout(saveTimer);
    await get().saveNow();
    set({ routing: true, statusText: 'Routage automatique en cours…' });
    try {
      const { report, project: fresh } = await api(`/projects/${project.id}/autoroute`, { method: 'POST', body: { nets } });
      set({
        project: { ...fresh, role: project.role },
        past: [...get().past.slice(-HISTORY_CAP + 1), project], future: [],
        statusText: `Routage : ${report.routed.length} net(s) routé(s)${report.failed.length ? `, ${report.failed.length} liaison(s) en échec (${[...new Set(report.failed.map((f) => f.net))].slice(0, 6).join(', ')})` : ''} — ${report.tracks} pistes, ${report.vias} vias, ${(report.ms / 1000).toFixed(1)} s`,
      });
      // Le serveur a enregistré : l'annulation repasse par une sauvegarde normale.
    } catch (e) {
      set({ statusText: `⚠ Routage impossible : ${e.message}` });
    } finally {
      set({ routing: false });
    }
  },

  // ── Sauvegarde automatique ──
  scheduleSave() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => get().saveNow(), 800);
  },
  async saveNow() {
    const { project } = get();
    if (!project || project.role === 'viewer') return;
    if (get().ai.job) { set({ saveState: 'paused' }); return; }
    set({ saveState: 'saving' });
    try {
      await api(`/projects/${project.id}`, { method: 'PUT', body: project });
      set({ saveState: 'saved' });
    } catch (e) {
      set({ saveState: 'error', statusText: `Sauvegarde impossible : ${e.message}` });
    }
  },

  // ── Assistant ──
  async loadChat() {
    const { project } = get();
    if (!project) return;
    api('/ai/status').then((st) => set({ ai: { ...get().ai, available: st.available } })).catch(() => {});
    const { messages, running } = await api(`/projects/${project.id}/chat`);
    set({ ai: { ...get().ai, messages } });
    if (running) get().attachJob(running.job);
  },
  setTier: (tier) => set({ ai: { ...get().ai, tier } }),
  async sendPrompt(prompt, images = [], docs = []) {
    const { project, ai } = get();
    if (!project || ai.job || !prompt.trim()) return;
    clearTimeout(saveTimer);
    await get().saveNow();
    const refs = project.components.filter((c) => get().selection.includes(c.id)).map((c) => c.ref);
    set({ ai: { ...get().ai, error: '', pending: prompt, pendingImages: images.map((im) => im.preview), pendingDocs: docs.map(({ id, name, size }) => ({ id, name, size })) } });
    try {
      const { job } = await api(`/projects/${project.id}/chat`, {
        method: 'POST',
        body: { prompt, tier: ai.tier, selection: refs, images: images.map(({ name, mediaType, data }) => ({ name, mediaType, data })), files: docs.map(({ name, data }) => ({ name, data })) },
      });
      get().attachJob(job);
    } catch (e) {
      set({ ai: { ...get().ai, error: e.message, pending: null } });
    }
  },
  attachJob(job) {
    eventSource?.close();
    aiSnapshotPushed = false;
    set({ ai: { ...get().ai, job, text: '', status: 'Connexion…', tools: [] } });
    const es = new EventSource(`/api/ai/jobs/${job}/events`);
    eventSource = es;
    es.onmessage = (m) => {
      let ev;
      try { ev = JSON.parse(m.data); } catch { return; }
      const ai = get().ai;
      if (ev.kind === 'resume') set({ ai: { ...ai, text: ev.text, tools: ev.tools, status: ev.status } });
      else if (ev.kind === 'text') set({ ai: { ...ai, text: ai.text + ev.text } });
      else if (ev.kind === 'status') set({ ai: { ...ai, status: ev.text } });
      else if (ev.kind === 'tool') set({ ai: { ...ai, tools: [...ai.tools, ev.name] } });
      else if (ev.kind === 'toolResult' && !ev.ok) set({ ai: { ...ai, status: `Correction : ${ev.error}` } });
      else if (ev.kind === 'project') get().applyServerProject(ev.project);
      else if (ev.kind === 'done' || ev.kind === 'error') get().endJob(ev.kind === 'error' ? ev.message : '');
    };
    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED || get().ai.job === job) {
        es.close();
        setTimeout(() => {
          if (get().ai.job === job) {
            api(`/projects/${get().project?.id}/chat`).then(({ running }) => {
              if (running?.job === job) get().attachJob(job);
              else get().endJob('');
            }).catch(() => get().endJob(''));
          }
        }, 1500);
      }
    };
  },
  /** Projet modifié par l'assistant : un seul cran d'historique par tour. */
  applyServerProject(project) {
    const cur = get().project;
    if (!cur || cur.id !== project.id) return;
    const past = aiSnapshotPushed ? get().past : [...get().past.slice(-HISTORY_CAP + 1), cur];
    aiSnapshotPushed = true;
    set({ project: { ...project, role: cur.role }, past, future: [] });
    get().pruneSelection();
  },
  async endJob(error) {
    eventSource?.close();
    eventSource = null;
    set({ ai: { ...get().ai, job: null, status: '', error: error || '', pending: null, pendingDocs: null } });
    const { project } = get();
    if (project) {
      try {
        const fresh = await api(`/projects/${project.id}`);
        if (aiSnapshotPushed) set({ project: fresh });
        set({ saveState: 'saved' });
      } catch { /* garde la copie locale */ }
      await get().loadChat().catch(() => {});
    }
  },
  async cancelJob() {
    const { job } = get().ai;
    if (job) await api(`/ai/jobs/${job}/cancel`, { method: 'POST' }).catch(() => {});
  },
  async clearChat() {
    const { project } = get();
    await api(`/projects/${project.id}/chat`, { method: 'DELETE' });
    set({ ai: { ...get().ai, messages: [] } });
  },
}));
